
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';

interface GuestSelectorProps {
  value: string;
  onChange: (value: string) => void;
  maxGuests?: number;
  className?: string;
  placeholder?: string;
}

const GuestSelector = ({ 
  value, 
  onChange, 
  maxGuests = 6, 
  className = "",
  placeholder = "1 huésped" 
}: GuestSelectorProps) => {
  const deviceInfo = useDeviceDetection();
  const [localValue, setLocalValue] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Normalize and validate the value
  const normalizedValue = useMemo(() => {
    if (!value || value === '' || value === 'undefined' || value === 'null') {
      return '1';
    }
    
    // Clean any duplicated text (e.g., "1 huesped1 huesped" -> "1")
    const cleanValue = value.toString().replace(/[^\d]/g, '');
    const numericValue = parseInt(cleanValue) || 1;
    
    // Ensure value is within valid range
    if (numericValue < 1) return '1';
    if (numericValue > maxGuests) return maxGuests.toString();
    
    return numericValue.toString();
  }, [value, maxGuests]);

  // Sync local value with normalized value
  useEffect(() => {
    if (!isUpdating) {
      setLocalValue(normalizedValue);
    }
  }, [normalizedValue, isUpdating]);

  // Stable change handler with mobile-specific logic
  const handleValueChange = useCallback((newValue: string) => {
    console.log('🔄 GuestSelector value changing:', { from: value, to: newValue, isMobile: deviceInfo.isMobile });
    
    setIsUpdating(true);
    setLocalValue(newValue);
    
    // Add slight delay for mobile Chrome to prevent DOM manipulation conflicts
    if (deviceInfo.isMobile && deviceInfo.isAndroid) {
      setTimeout(() => {
        try {
          onChange(newValue);
          setIsUpdating(false);
        } catch (error) {
          console.error('❌ Error in guest selector change:', error);
          // Recovery: reset to safe value
          setLocalValue('1');
          onChange('1');
          setIsUpdating(false);
        }
      }, 50);
    } else {
      try {
        onChange(newValue);
        setIsUpdating(false);
      } catch (error) {
        console.error('❌ Error in guest selector change:', error);
        setLocalValue('1');
        onChange('1');
        setIsUpdating(false);
      }
    }
  }, [onChange, deviceInfo.isMobile, deviceInfo.isAndroid, value]);

  // Generate unique key for mobile Chrome to force clean re-mount when needed
  const selectKey = useMemo(() => {
    if (deviceInfo.isMobile && deviceInfo.isAndroid) {
      return `guest-select-${normalizedValue}-${Date.now() % 1000}`;
    }
    return `guest-select-${normalizedValue}`;
  }, [normalizedValue, deviceInfo.isMobile, deviceInfo.isAndroid]);

  const displayValue = isUpdating ? localValue : normalizedValue;

  return (
    <Select 
      key={selectKey}
      value={displayValue} 
      onValueChange={handleValueChange}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent 
        sideOffset={4}
        align="start"
        position="popper"
        className="z-50 bg-white"
      >
        {Array.from({ length: maxGuests }, (_, index) => {
          const guestCount = index + 1;
          const label = guestCount === 1 ? '1 huésped' : `${guestCount} huéspedes`;
          
          return (
            <SelectItem 
              key={`guest-${guestCount}`} 
              value={guestCount.toString()}
            >
              {label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

export default GuestSelector;
